'use client';

import { isSearchSsrEnabled } from '@lib/util/flags';
import Cookie from 'js-cookie';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

/**
 * Whether the /search route will serve the SSR page, read from the SAME source
 * the route itself reads: the middleware-written `as-experiment-variants`
 * cookie (via isSearchSsrEnabled). The client Amplitude SDK evaluates the flag
 * independently of the middleware's remote evaluation, so deciding the URL
 * format from useFlags could emit a `?q=` URL to a CSR-rendered page — the
 * route's CsrFallback bridge recovers it, but only through a full server
 * round-trip where the hash is a soft push; the cookie keeps both decisions
 * in lock-step. Read at call time — not render time — so client
 * components can server-render without a hydration mismatch and always use
 * the freshest cookie. A missing/blocked cookie means the route serves CSR,
 * and the hash format is exactly what that page consumes.
 */
const isSsrSearch = (): boolean =>
  isSearchSsrEnabled({
    get: (name: string) => {
      const value = Cookie.get(name);
      return value === undefined ? undefined : { value };
    },
  });

/**
 * Builds the /search URL for a text query in the format the active search page
 * consumes. The SSR page (`as-search-ssr` treatment) reads the query string, so
 * `/search?q=…` lets the server render the right results in a single query; the
 * CSR page's UrlManager only reads the hash, so it keeps `/search#q=…`.
 */
export const useSearchQueryUrl = () => {
  return useCallback(
    (query: string) =>
      `/search${isSsrSearch() ? '?' : '#'}q=${encodeURIComponent(query)}`,
    []
  );
};

/**
 * Navigates to /search for a text query. The SSR path needs a FULL navigation
 * (never router.push): the SSR page seeds `q` server-side, and during a soft
 * transition its SearchParameterManager can serialize engine state onto the
 * previous page's still-live URL — rewriting history to `<old-path>?q=…` and
 * silently clearing the query (same finding as POC commit 3864cbb3, see
 * specs/coveo-ssr/10-feature-ssr-header-search.md). The CSR path keeps the
 * soft push to the hash URL.
 */
export const useNavigateToSearch = () => {
  const router = useRouter();
  const buildSearchQueryUrl = useSearchQueryUrl();

  return useCallback(
    (query: string) => {
      const url = buildSearchQueryUrl(query);
      if (isSsrSearch()) {
        window.location.assign(url);
      } else {
        router.push(url);
      }
    },
    [buildSearchQueryUrl, router]
  );
};
